import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import Button from "../components/Button";
import PetHeader from "../components/PetHeader";
import PetList from "../components/PetList";
import StickyScrollWrapper from "../components/StickyScrollWrapper";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/Accordion";
import { getPetByID } from "../services/api";
import { getCountryByCode } from "../services/utils";

const staticUrl: string = process.env.STATIC_URL;

export default function Shelter() {
  const [loading, setLoading] = useState(true);
  const [pet, setPet] = useState({});
  const [shelter, setShelter] = useState({});
  const navigate = useNavigate();
  const petListRef = useRef<HTMLDivElement | null>(null);
  const { id } = useParams<{
    id: string;
  }>();

  useEffect(() => {
    void getPetByID(id)
      .then((data) => {
        setPet(data);
        setShelter(data.user.profile);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const getNav = () => {
    return [
      { title: "About dog", url: `/pet/${id}` },
      { title: "About shelter", url: `/pet/${id}/shelter`, active: true },
    ];
  };

  if (loading) {
    return null;
  }

  return (
    <>
      <div className="container mx-auto px-5">
        <div className="mb-6 text-theme-grey-7">
          <Link to="/">Home</Link> / <span className="text-theme-grey-11">{pet.name}</span>
        </div>
        <PetHeader pet={pet}>
          <Button
            title="Explore Adoption"
            onClick={() => navigate(`/adopt/${pet.id}`)}
            theme="dark"
          />
        </PetHeader>
      </div>

      <div className="relative my-8 border-b-[1px] border-theme-grey-1">
        <div className="container mx-auto flex flex-col items-center justify-between gap-4 px-5 lg:flex-row">
          <div className="flex h-full w-full flex-row items-center gap-6">
            {getNav().map((i) => (
              <div key={i.title} className="flex h-full items-center">
                <Link
                  to={i.url}
                  className={`cursor-pointer border-b-2 border-transparent pb-1 font-semibold text-theme-grey-11 hover:border-[#000000] ${
                    i.active && "!border-[#000000]"
                  }`}
                >
                  {i.title}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="container mx-auto mb-8 px-5">
        <div className="relative flex flex-col gap-4">
          <img src={`${staticUrl}${shelter.logo.url}`} className="h-auto w-[110px] rounded-[4px]" />
          <div className="text-[30px] font-medium leading-none text-theme-grey-9.5 lg:text-[64px]">
            {shelter.title}
          </div>
          <div className="text-[20px] font-semibold text-theme-grey-9.5">
            {getCountryByCode(shelter.country)}
          </div>
        </div>
      </div>

      <div className="container mx-auto mb-8 px-5">
        <div className="mb-10 grid grid-cols-3 gap-5">
          <div className="col-span-2">
            <img
              src={`${staticUrl}${shelter.header.url}`}
              className="h-auto w-full rounded-[4px]"
            />
          </div>
          <div>
            <StickyScrollWrapper refObj={petListRef}>
              <Accordion type="single" collapsible className="w-full" defaultValue="item-1">
                <AccordionItem value="item-1" className="bg-theme-grey-1 px-5">
                  <AccordionTrigger>About shelter</AccordionTrigger>
                  <AccordionContent>
                    <div
                      className="flex flex-col gap-6 whitespace-pre-wrap leading-[135%] text-[#5C5C5C]"
                      dangerouslySetInnerHTML={{
                        __html: shelter.desc,
                      }}
                    />
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="item-2" className="bg-theme-grey-1 px-5">
                  <AccordionTrigger>Contact</AccordionTrigger>
                  <AccordionContent>
                    <div className="flex flex-col gap-5">
                      {[
                        {
                          title: "Address",
                          value: `${shelter.street}, ${shelter.city}, ${shelter.postcode}, ${shelter.country}`,
                        },
                        { title: "Telephone", value: shelter.phone },
                        { title: "Website", value: shelter.website },
                      ].map((p) => (
                        <div key={p.title} className="text-[#868686]">
                          <div className="font-barlowmedium text-[#242424]">{p.title}</div>
                          {p.value}
                        </div>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </StickyScrollWrapper>
          </div>
        </div>
      </div>

      <div className="container mx-auto mb-8" id="dogs-list" ref={petListRef}>
        <div className="mb-8 px-5 text-[24px] leading-none text-[#161616]">
          All dogs from {shelter.title}
        </div>
        <PetList showFilter={false} />
      </div>
    </>
  );
}
